/**
 * AlertElement — F12.H T12.H.6.
 *
 * Komunikat inline z 4 wariantami: info / success / warning / error.
 * Może mieć tytuł, treść, ikonę i być dismissible (zamykalny w trybie form).
 *
 * Renderuje przez wspólny prymityw `<Alert>` (styl `subtle`) — jedno źródło prawdy dla
 * tonów i tokenów semantycznych. Dzięki temu „info" jest STAŁYM niebieskim (token
 * `--color-info`), a nie kolorem akcentu tenanta (dawny `accent-*` zmieniał się z motywem).
 *
 * Różnica vs HighlightElement (T12.H.2):
 *  - Highlight to wyróżnione info na rekordzie (sticky, bardziej dekoracyjne).
 *  - Alert to standardowy komunikat inline (info/ostrzeżenie/błąd/sukces),
 *    typowo używany pod polami lub w sekcjach do informowania.
 *
 * Settings shape:
 *   { variant: 'info'|'success'|'warning'|'error',
 *     title?: string, content: string,
 *     dismissible?: boolean,
 *     icon?: string }
 */

import { useState } from 'react';

import { Alert, type AlertTone } from '@/components/Alert';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';

type AlertVariant = AlertTone;

function AlertElementBody({
  variant,
  title,
  content,
  dismissible,
  elementId,
  mode,
}: {
  variant: AlertVariant;
  title?: string;
  content: string;
  dismissible: boolean;
  elementId: string;
  mode: 'form' | 'preview' | 'builder';
}) {
  const [dismissed, setDismissed] = useState(false);

  // W trybie builder zawsze pokazujemy (user musi widzieć element żeby go edytować)
  if (dismissed && mode !== 'builder') return null;

  // Zamykanie działa tylko poza builderem (w builderze element musi zostać widoczny do edycji).
  const canDismiss = dismissible && mode !== 'builder';
  const body = content || (mode === 'builder' ? '(pusta treść alertu)' : '');

  return (
    <Alert
      tone={variant}
      variant="subtle"
      title={title}
      onDismiss={canDismiss ? () => setDismissed(true) : undefined}
      dismissLabel="Zamknij komunikat"
      data-testid={`alert-element-${elementId}`}
      data-element-type="alert"
      data-variant={variant}
    >
      {body}
    </Alert>
  );
}

export const AlertElement: LayoutElementDefinition = {
  type: 'alert',
  category: 'komunikaty',
  isContainer: false,
  label: 'Alert',
  icon: 'AlertCircle',
  defaultSettings: () => ({
    variant: 'info',
    title: '',
    content: 'Treść komunikatu',
    dismissible: false,
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const variant = (settings.variant as AlertVariant) ?? 'info';
    const title = String(settings.title ?? '');
    const content = String(settings.content ?? '');
    const dismissible = settings.dismissible === true;
    return (
      <div className="space-y-3" data-testid="alert-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Wariant</label>
          <Dropdown
            value={variant}
            onChange={(v) => onChange({ variant: v as AlertVariant })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="alert-prop-variant"
            options={[
              { value: 'info', label: 'Informacja' },
              { value: 'success', label: 'Sukces' },
              { value: 'warning', label: 'Ostrzeżenie' },
              { value: 'error', label: 'Błąd' },
            ]}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Tytuł (opcjonalnie)
          </label>
          <Input
            type="text"
            value={title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="alert-prop-title"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Treść</label>
          <Textarea
            value={content}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={3}
            data-testid="alert-prop-content"
          />
        </div>
        <Checkbox
          checked={dismissible}
          onChange={(e) => onChange({ dismissible: e.target.checked })}
          data-testid="alert-prop-dismissible"
          label="Możliwość zamknięcia przez użytkownika"
        />
      </div>
    );
  },
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as {
      variant?: AlertVariant;
      title?: string;
      content?: string;
      dismissible?: boolean;
    };
    return (
      <AlertElementBody
        variant={settings.variant ?? 'info'}
        title={settings.title?.trim() || undefined}
        content={settings.content ?? ''}
        dismissible={settings.dismissible === true}
        elementId={element.id}
        mode={mode}
      />
    );
  },
};
